import { invoke } from "./invoke";
import { displayFileName } from "./fileNames";
import {
  isJust,
  just,
  nothing,
  type Maybe,
  matchMaybe,
  toMaybe,
} from "./maybe";
import { trimmedMaybe, trimmedOrEmpty } from "./trimmedString";

export type FileHistoryEntry = {
  oid: string;
  message: string;
  /** Unix seconds. */
  time: number;
  renamedFrom: Maybe<string>;
  renamedTo: Maybe<string>;
};

export type FileChangePayload = {
  oid: string;
  message: string;
  time: number;
  after: string;
  before: string;
  renamedFrom: Maybe<string>;
  renamedTo: Maybe<string>;
};

function wireStringField(obj: Record<string, unknown>, key: string): unknown {
  return obj[key];
}

function normalizeHistoryEntry(raw: Record<string, unknown>): FileHistoryEntry {
  return {
    oid: String(raw.oid ?? ""),
    message: String(raw.message ?? ""),
    time: Number(raw.time ?? 0),
    renamedFrom: trimmedMaybe(wireStringField(raw, "renamedFrom") as string),
    renamedTo: trimmedMaybe(wireStringField(raw, "renamedTo") as string),
  };
}

function normalizeChangePayload(
  raw: Record<string, unknown>,
): FileChangePayload {
  return {
    oid: String(raw.oid ?? ""),
    message: String(raw.message ?? ""),
    time: Number(raw.time ?? 0),
    after: String(raw.after ?? ""),
    before: String(raw.before ?? ""),
    renamedFrom: trimmedMaybe(wireStringField(raw, "renamedFrom") as string),
    renamedTo: trimmedMaybe(wireStringField(raw, "renamedTo") as string),
  };
}

/** Absolute path → project-relative path for git pathspecs. */
export function projectRelativePath(
  rootPath: string,
  absolutePath: string,
): Maybe<string> {
  const norm = (p: string): string => p.replace(/\\/g, "/");
  const root = norm(rootPath).replace(/\/+$/, "");
  const abs = norm(absolutePath);
  if (abs === root) return nothing;
  if (abs.startsWith(`${root}/`) === false) return nothing;
  return just(abs.slice(root.length + 1));
}

export async function listFileHistory(
  rootPath: string,
  relativePath: string,
  limit = 50,
): Promise<FileHistoryEntry[]> {
  const raw = await invoke<Record<string, unknown>[]>("list_file_history", {
    rootPath,
    relativePath,
    limit,
  });
  return raw.map(normalizeHistoryEntry);
}

export async function getFileChange(
  rootPath: string,
  relativePath: string,
  oid: string,
): Promise<FileChangePayload> {
  const raw = await invoke<Record<string, unknown>>("get_file_change", {
    rootPath,
    relativePath,
    oid,
  });
  return normalizeChangePayload(raw);
}

const LOCALE = "en-US";

/** Relative clock phrasing for the timeline. */
export function formatChangeWhen(
  unixSeconds: number,
  nowMs = Date.now(),
): string {
  const then = unixSeconds * 1000;
  const diffSec = Math.round((nowMs - then) / 1000);
  if (diffSec < 45) return "Just now";
  if (diffSec < 3600) {
    const m = Math.max(1, Math.round(diffSec / 60));
    return m === 1 ? "1 minute ago" : `${m} minutes ago`;
  }
  if (diffSec < 86400) {
    const h = Math.max(1, Math.round(diffSec / 3600));
    return h === 1 ? "1 hour ago" : `${h} hours ago`;
  }

  const date = new Date(then);
  const now = new Date(nowMs);
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  );
  const startOfThen = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );
  const dayDiff = Math.round(
    (startOfToday.getTime() - startOfThen.getTime()) / 86400000,
  );
  const time = date.toLocaleTimeString(LOCALE, {
    hour: "numeric",
    minute: "2-digit",
  });
  if (dayDiff === 1) return `Yesterday · ${time}`;
  if (dayDiff < 7) {
    const weekday = date.toLocaleDateString(LOCALE, { weekday: "long" });
    return `${weekday} · ${time}`;
  }
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString(LOCALE, {
      month: "short",
      day: "numeric",
    });
  }
  return date.toLocaleDateString(LOCALE, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Parse `Rename file: Old.md → New.md` / `Rename templates: a → b` style messages.
 */
export function parseRenamePaths(
  message: string,
): Maybe<{ from: string; to: string }> {
  const match = message
    .trim()
    .match(/^Rename(?:\s+[\w\s]+?)?:\s*(.+?)\s*→\s*(.+)\s*$/i);
  if (match === null) return nothing;
  const fromM = trimmedMaybe(match[1] ?? null);
  const toM = trimmedMaybe(match[2] ?? null);
  if (isJust(fromM) && isJust(toM)) {
    return just({ from: fromM.value, to: toM.value });
  }
  return nothing;
}

/** Writer-facing "Old → New" label for a rename, without file extensions. */
export function formatRenameDetail(
  from: Maybe<string>,
  to: Maybe<string>,
  message?: string,
): Maybe<string> {
  let fromPath = matchMaybe(from, {
    nothing: () => "",
    just: (p) => trimmedOrEmpty(p),
  });
  let toPath = matchMaybe(to, {
    nothing: () => "",
    just: (p) => trimmedOrEmpty(p),
  });
  const messageM = toMaybe(message);
  if (
    (fromPath.length === 0 || toPath.length === 0) &&
    isJust(messageM) &&
    messageM.value.length > 0
  ) {
    const parsedM = parseRenamePaths(messageM.value);
    if (isJust(parsedM)) {
      if (fromPath.length === 0) fromPath = parsedM.value.from;
      if (toPath.length === 0) toPath = parsedM.value.to;
    }
  }
  if (fromPath.length === 0 || toPath.length === 0) return nothing;
  return just(`${displayFileName(fromPath)} → ${displayFileName(toPath)}`);
}

/**
 * Map internal commit messages to writer-facing titles.
 * Most history is "Autosave"; keep the rest human when possible.
 */
export function humanizeChangeTitle(message: string): string {
  const trimmed = message.trim();
  const lower = trimmed.toLowerCase();
  if (lower === "autosave") return "Autosaved";
  if (lower === "create project") return "Started this manuscript";
  if (
    lower === "restored from trash" ||
    lower.startsWith("restore file:") ||
    lower.startsWith("restore ")
  ) {
    return "Restored from trash";
  }
  if (lower.startsWith("rename ") || isJust(parseRenamePaths(trimmed))) {
    return "Renamed";
  }
  if (lower.startsWith("delete ")) return "Removed";
  if (lower.startsWith("create ") || lower.startsWith("new ")) return "Created";
  if (lower.includes("setting") || lower.includes("config")) {
    return "Updated project settings";
  }
  if (lower.includes("template")) return "Updated template";
  if (lower.includes("note")) return "Updated note";
  if (lower.includes("database") || lower.includes("entity")) {
    return "Updated story bible";
  }
  if (
    lower.includes("asset") ||
    lower.includes("cover") ||
    lower.includes("image")
  ) {
    return "Updated images";
  }
  if (
    trimmed.length > 0 &&
    trimmed.length <= 48 &&
    /^[0-9a-f]{7,}$/i.test(trimmed) === false
  ) {
    return trimmed;
  }
  return "Saved changes";
}

export function formatChangeSummary(added: number, removed: number): string {
  const parts: string[] = [];
  if (added > 0) {
    parts.push(added === 1 ? "1 word added" : `${added} words added`);
  }
  if (removed > 0) {
    parts.push(removed === 1 ? "1 word removed" : `${removed} words removed`);
  }
  if (parts.length === 0) return "No wording changes";
  return parts.join(" · ");
}
